import React, {
  useState, useCallback, useRef,
} from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import CallView from 'views/Call';
import IncomingCallService from 'api/IncomingCall';
import AccountDirectDialService from 'api/AccountDirectDial';
import { useFetch, useFetchTableData } from 'hooks';
import SettingsActions from 'store/reducers/Settings';
import { formatPhone, handlePromise, getCountryName } from 'utils';

const Call = ( {
  userRoles, toggleErrorAlert, toggleLoading, locale,
} ) => {
  const [selectedRowsChange, setSelectedRowsChange] = useState( new Date().valueOf() );
  const [dataReloaded, setDataReloaded] = useState( new Date().valueOf() );
  const selectedRows = useRef( [] );
  const apiParams = useRef( {} );

  const [fetchData, data, dataLoading] = useFetchTableData( {
    promise: IncomingCallService.getIncomingCalls,
    toggleErrorAlert,
    deps: [dataReloaded],
    callback: ( params ) => {
      if ( params ) {
        apiParams.current = params;
        delete apiParams.current.offset;
        delete apiParams.current.limit;
      }
      if ( selectedRows.current && selectedRows.current.length ) {
        selectedRows.current = [];
        setSelectedRowsChange( new Date().valueOf() );
      }
    },
  } );

  const [phoneNumbers, phoneNumbersLoading] = useFetch( {
    initialState: [],
    format: ( dataToFormat ) => _.map( dataToFormat.data,
      ( item ) => ( {
        id: item.id,
	name: `${formatPhone( item.directDial.number )} | ${getCountryName( item.directDial.country, locale )} - ${item.directDial.area}`,
      } ) ),
    promise: () => AccountDirectDialService.getDdis( { limit: -1 } ),
    deps: [userRoles],
  } );

  const toggleRowChecked = useCallback( ( id, value ) => {
    if ( value ) selectedRows.current.push( id );
    else {
      const index = selectedRows.current.indexOf( id );
      if ( index !== -1 ) selectedRows.current.splice( index, 1 );
    }
    setSelectedRowsChange( new Date().valueOf() );
  }, [] );

  const toggleAllRowsChecked = useCallback( ( value ) => {
    _.map( data.data, ( item ) => {
      const index = selectedRows.current.indexOf( item.id );
      if ( value ) {
        if ( index === -1 ) selectedRows.current.push( item.id );
      } else if ( index !== -1 ) selectedRows.current.splice( index, 1 );
    } );
    setSelectedRowsChange( new Date().valueOf() );
  }, [data] );

  const exportData = useCallback( async ( ) => {
    toggleLoading( true );
    const [errors, response, responseData] = await handlePromise(
      IncomingCallService.getIncomingCalls( { download: true, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...apiParams.current, 'filters[ids]': selectedRows.current } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return toggleErrorAlert( errors );
    window.open( responseData.uri );
  }, [toggleLoading, toggleErrorAlert] );

  const deleteSelectedRows = useCallback( async ( callback ) => {
    toggleLoading( true );
    const [errors, response] = await handlePromise(
      IncomingCallService.deleteSelectedIncomingCalls( { incomingCalls: selectedRows.current } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return toggleErrorAlert( errors );

    selectedRows.current = [];
    setDataReloaded( new Date().valueOf() );
    callback();
  }, [toggleLoading, toggleErrorAlert] );

  return (
    <CallView
      data={data}
      phoneNumbers={phoneNumbers}
      phoneNumbersLoading={phoneNumbersLoading}
      selectedRows={selectedRows.current}
      selectedRowsChange={selectedRowsChange}
      isLoading={dataLoading}
      onFetchData={fetchData}
      onToggleRowChecked={toggleRowChecked}
      onToggleAllRowsChecked={toggleAllRowsChecked}
      onExport={exportData}
      onDelete={deleteSelectedRows}
    />
  );
};

const mapStateToProps = ( { user, settings } ) => ( {
  userRoles: user.roles,
  locale: _.get( settings, 'locale.locale' ),
} );

const mapDispatchToProps = ( {
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleLoading: SettingsActions.toggleLoading,
} );

export default connect( mapStateToProps, mapDispatchToProps )( Call );
